export const parseAmount = (value) => {
  if (typeof value === "number") {
    return value;
  }
  return Number(String(value ?? 0).replace(/,/g, "")) || 0;
};

export const calcCustomerProfitPercent = (storePrice, consumerPrice) => {
  const store = parseAmount(storePrice);
  const consumer = parseAmount(consumerPrice);
  if (store <= 0) {
    return 0;
  }
  return Math.round(((consumer - store) / store) * 10000) / 100;
};

export const formatCustomerProfitPercent = (value) => {
  const num = Number(value) || 0;
  return `${num.toLocaleString(undefined, { maximumFractionDigits: 2 })}%`;
};

const mapUnitOption = (option) => {
  const sourceInventory = Number(
    option.source_inventory ?? option.inventory ?? 0
  );

  return {
    unit_level: Number(option.unit_level),
    label: option.label || option.measure_unit || "",
    unit_count: option.unit_count,
    unit_price: parseAmount(option.unit_price),
    store_price: parseAmount(option.store_price),
    consumer_price: parseAmount(option.consumer_price),
    source_inventory: sourceInventory,
    inventory: sourceInventory,
    discount_percent: option.discount_percent,
    customer_profit_percent:
      option.customer_profit_percent ??
      calcCustomerProfitPercent(option.store_price, option.consumer_price),
  };
};

export const getSourceInventory = (option, product = null) => {
  if (option?.source_inventory != null) {
    return Number(option.source_inventory) || 0;
  }

  if (option?.inventory != null) {
    return Number(option.inventory) || 0;
  }

  return Number(product?.inventory ?? 0) || 0;
};

const withRemainingInventory = (product, unitOptions) => {
  return unitOptions.map((option) => {
    const sourceInventory = getSourceInventory(option, product);
    const orderedCount = product.unitCounts?.[option.unit_level] ?? 0;

    return {
      ...option,
      source_inventory: sourceInventory,
      inventory: Math.max(0, sourceInventory - orderedCount),
    };
  });
};

export const getRawOrderUnitOptions = (product) => {
  return (
    product?.order_unit_options ||
    product?.orderUnitOptions ||
    product?.OrderUnitOptions ||
    []
  );
};

export const getRawUnitPrices = (product) => {
  const prices =
    product?.UnitPrices ||
    product?.unit_prices ||
    product?.unitPrices ||
    [];

  return Array.isArray(prices) ? prices : [];
};

export const buildUnitOptionsFromLegacy = (product) => {
  const options = [];
  const seenLevels = new Set();

  const addOption = (option) => {
    const level = Number(option?.unit_level);
    if (!level || seenLevels.has(level)) {
      return;
    }
    seenLevels.add(level);
    options.push(
      mapUnitOption({
        ...option,
        label: option.label || option.measure_unit || `واحد ${level}`,
      })
    );
  };

  addOption({
    unit_level: 1,
    label:
      product.measure_unit?.name ||
      product.MeasureUnit?.name ||
      product.measureUnit ||
      "",
    unit_count: product.unit_count,
    unit_price: product.unit_price,
    store_price: product.store_price,
    consumer_price: product.consumer_price,
    inventory: product.inventory,
    discount_percent: product.discount_percent,
  });

  getRawUnitPrices(product).forEach((price) => {
    const level = Number(price.unit_level);
    if (level <= 1) {
      return;
    }

    addOption({
      unit_level: level,
      label: price.measure_unit || price.label || "",
      unit_count: price.unit_count,
      unit_price: price.unit_price,
      store_price: price.store_price,
      consumer_price: price.consumer_price,
      inventory: price.inventory ?? product.inventory,
      discount_percent: price.discount_percent ?? product.discount_percent,
    });
  });

  return options.sort((a, b) => a.unit_level - b.unit_level);
};

export const getUnitOptions = (product) => {
  const merged = new Map();

  getRawOrderUnitOptions(product).forEach((option) => {
    const mapped = mapUnitOption({
      ...option,
      label: option.label || option.measure_unit || `واحد ${option.unit_level}`,
    });
    merged.set(mapped.unit_level, mapped);
  });

  buildUnitOptionsFromLegacy(product).forEach((option) => {
    if (!merged.has(option.unit_level)) {
      merged.set(option.unit_level, option);
    }
  });

  const options = Array.from(merged.values()).sort(
    (a, b) => a.unit_level - b.unit_level
  );

  const declaredCount = Number(product?.units_count ?? 0);
  if (declaredCount >= 2 && options.length < declaredCount && options.length >= 2) {
    return options;
  }

  return options;
};

export const hasMultipleUnits = (product) => {
  return getUnitOptions(product).length > 1;
};

export const getBaseUnitPrice = (product, unitLevel) => {
  const options = product.unitOptions?.length
    ? product.unitOptions
    : getUnitOptions(product);
  const option = options.find((item) => item.unit_level === unitLevel);

  return option?.unit_price ?? parseAmount(product.unit_price);
};

export const getEffectiveUnitPrice = (product, unitLevel, option = null) => {
  const custom = product.customUnitPrices?.[unitLevel];
  if (custom != null && custom !== "") {
    return parseAmount(custom);
  }

  if (option) {
    return option.unit_price;
  }

  return getBaseUnitPrice(product, unitLevel);
};

export const mapProductFromApi = (item) => {
  const unitOptions = getUnitOptions(item);
  const unitCounts = {};
  unitOptions.forEach((opt) => {
    unitCounts[opt.unit_level] = 0;
  });

  const product = {
    ...item,
    unitOptions,
    selectedUnitLevel: unitOptions[0]?.unit_level ?? 1,
    unitCounts,
    customUnitPrices: item.customUnitPrices || {},
    num: 0,
  };

  return applySelectedUnitToDisplay(product);
};

export const getActiveUnitOption = (product) => {
  const options = product.unitOptions?.length
    ? product.unitOptions
    : getUnitOptions(product);

  return (
    options.find((option) => option.unit_level === product.selectedUnitLevel) ||
    options[0]
  );
};

export const applySelectedUnitToDisplay = (product) => {
  const baseUnitOptions = product.unitOptions?.length
    ? product.unitOptions
    : getUnitOptions(product);

  if (!baseUnitOptions.length) {
    return product;
  }

  const unitOptions = withRemainingInventory(product, baseUnitOptions);
  const active =
    unitOptions.find(
      (option) => option.unit_level === product.selectedUnitLevel
    ) || unitOptions[0];

  if (!active) {
    return product;
  }

  const num = product.unitCounts?.[product.selectedUnitLevel] ?? product.num ?? 0;
  const unitPrice = getEffectiveUnitPrice(
    product,
    product.selectedUnitLevel,
    active
  );

  return {
    ...product,
    unitOptions,
    unit_price: unitPrice,
    store_price: active.store_price,
    consumer_price: active.consumer_price,
    unit_count: active.unit_count,
    inventory: active.inventory,
    measure_unit: { name: active.label },
    discount_percent: active.discount_percent ?? product.discount_percent,
    customer_profit_percent:
      active.customer_profit_percent ??
      calcCustomerProfitPercent(active.store_price, active.consumer_price),
    num,
  };
};

export const changeProductUnit = (productList, productId, unitLevel) => {
  return productList.map((item) => {
    if (item.id !== productId) {
      return item;
    }

    const unitCounts = {
      ...item.unitCounts,
      [item.selectedUnitLevel]: item.num ?? 0,
    };

    return applySelectedUnitToDisplay({
      ...item,
      unitCounts,
      selectedUnitLevel: unitLevel,
    });
  });
};

export const increaseProductCount = (productList, productId) => {
  let limitHit = false;

  const nextList = productList.map((item) => {
    if (item.id !== productId) {
      return item;
    }

    const active = getActiveUnitOption(item);
    const current = item.unitCounts?.[item.selectedUnitLevel] ?? item.num ?? 0;
    const inventory = getSourceInventory(active, item);
    const maxOrder = item.max_order;

    if (maxOrder && (current >= maxOrder || current >= inventory)) {
      limitHit = true;
      return item;
    }

    if (!maxOrder && current >= inventory) {
      limitHit = true;
      return item;
    }

    const unitCounts = {
      ...item.unitCounts,
      [item.selectedUnitLevel]: current + 1,
    };

    return applySelectedUnitToDisplay({
      ...item,
      unitCounts,
    });
  });

  return { productList: nextList, limitHit };
};

export const decreaseProductCount = (productList, productId) => {
  return productList.map((item) => {
    if (item.id !== productId) {
      return item;
    }

    const current = item.unitCounts?.[item.selectedUnitLevel] ?? item.num ?? 0;
    if (current <= 0) {
      return item;
    }

    const unitCounts = {
      ...item.unitCounts,
      [item.selectedUnitLevel]: current - 1,
    };

    return applySelectedUnitToDisplay({
      ...item,
      unitCounts,
    });
  });
};

export const setProductCount = (productList, productId, nextCount) => {
  let limitHit = false;
  const requested = Math.max(0, Math.floor(Number(nextCount) || 0));

  const nextList = productList.map((item) => {
    if (item.id !== productId) {
      return item;
    }

    const active = getActiveUnitOption(item);
    const inventory = getSourceInventory(active, item);
    const maxOrder = item.max_order;
    let finalCount = requested;

    if (maxOrder && finalCount > maxOrder) {
      finalCount = maxOrder;
      limitHit = true;
    }

    if (finalCount > inventory) {
      finalCount = inventory;
      limitHit = true;
    }

    const unitCounts = {
      ...item.unitCounts,
      [item.selectedUnitLevel]: finalCount,
    };

    return applySelectedUnitToDisplay({
      ...item,
      unitCounts,
    });
  });

  return { productList: nextList, limitHit };
};

export const setProductUnitPrice = (productList, productId, nextPrice) => {
  return productList.map((item) => {
    if (item.id !== productId) {
      return item;
    }

    const level = item.selectedUnitLevel;
    const basePrice = getBaseUnitPrice(item, level);
    const parsed = parseAmount(nextPrice);
    const customUnitPrices = { ...(item.customUnitPrices || {}) };

    if (!Number.isFinite(parsed) || parsed === basePrice) {
      delete customUnitPrices[level];
    } else {
      customUnitPrices[level] = parsed;
    }

    return applySelectedUnitToDisplay({
      ...item,
      customUnitPrices,
    });
  });
};

export const calcCartTotal = (productList) => {
  let total = 0;

  productList.forEach((item) => {
    const options = item.unitOptions?.length
      ? item.unitOptions
      : getUnitOptions(item);

    options.forEach((opt) => {
      const count = item.unitCounts?.[opt.unit_level] ?? 0;
      total += count * getEffectiveUnitPrice(item, opt.unit_level, opt);
    });
  });

  return total;
};

export const resetProductCartCounts = (productList) =>
  productList.map((item) => {
    const unitOptions = item.unitOptions?.length
      ? item.unitOptions
      : getUnitOptions(item);
    const unitCounts = {};
    unitOptions.forEach((opt) => {
      unitCounts[opt.unit_level] = 0;
    });

    return applySelectedUnitToDisplay({
      ...item,
      unitCounts,
      num: 0,
    });
  });

export const mapProductFromApiWithOrder = (item) => {
  const product = mapProductFromApi(item);
  const orderCount = Number(item.order_count ?? 0);
  const unitLevel = Number(item.order_unit_level ?? product.selectedUnitLevel ?? 1);

  if (orderCount > 0) {
    const unitCounts = { ...product.unitCounts, [unitLevel]: orderCount };
    return applySelectedUnitToDisplay({
      ...product,
      unitCounts,
      selectedUnitLevel: unitLevel,
    });
  }

  return product;
};

export const buildOrderItems = (productList, customerId = null) => {
  const items = [];

  productList.forEach((item) => {
    const options = item.unitOptions?.length
      ? item.unitOptions
      : getUnitOptions(item);

    options.forEach((opt) => {
      const count = item.unitCounts?.[opt.unit_level] ?? 0;
      if (count > 0) {
        const line = {
          product_id: item.id,
          product_count: count,
          unit_level: opt.unit_level,
        };
        const basePrice = opt.unit_price;
        const effectivePrice = getEffectiveUnitPrice(item, opt.unit_level, opt);
        if (effectivePrice !== basePrice) {
          line.unit_price = effectivePrice;
        }
        if (customerId) {
          line.customer_id = customerId;
        }
        items.push(line);
      }
    });
  });

  return items;
};

export const enrichProductsWithUnitOptions = async (products, shopId, axios, baseUrl) => {
  const needsFetch = products.filter(
    (product) =>
      Number(product.units_count ?? 0) >= 2 &&
      getUnitOptions(product).length <= 1
  );

  if (!needsFetch.length || !shopId) {
    return products;
  }

  const enriched = [...products];

  await Promise.all(
    needsFetch.map(async (product) => {
      try {
        const response = await axios.get(
          `${baseUrl}/shops/${shopId}/products/${product.id}`
        );
        const detail = response?.data?.data;
        if (!detail) {
          return;
        }

        const index = enriched.findIndex((item) => item.id === product.id);
        if (index === -1) {
          return;
        }

        const merged = mapProductFromApi({
          ...product,
          ...detail,
          unitCounts: product.unitCounts,
          selectedUnitLevel: product.selectedUnitLevel,
          customUnitPrices: product.customUnitPrices,
          num: product.num,
        });

        if (getUnitOptions(merged).length > 1) {
          enriched[index] = merged;
        }
      } catch (error) {
        // ignore per-product fetch errors
      }
    })
  );

  return enriched;
};
